import { createTheme, ThemeOptions } from '@mui/material/styles';

// Primary Blue color palette (Professional fuel industry colors)
const bluePalette = {
  50: '#E3F2FD',
  100: '#BBDEFB',
  200: '#90CAF9',
  300: '#64B5F6',
  400: '#42A5F5',
  500: '#2196F3', // Main blue - professional and readable
  600: '#1E88E5',
  700: '#1976D2',
  800: '#1565C0',
  900: '#0D47A1',
};

const greenPalette = {
  50: '#E8F5E8',
  100: '#C8E6C8',
  200: '#A5D6A5',
  300: '#81C784',
  400: '#66BB6A',
  500: '#4CAF50', // Main green
  600: '#43A047',
  700: '#388E3C',
  800: '#2E7D32',
  900: '#1B5E20',
};

// Renaissance Africa Energy Company brand colors
const renaissanceColors = {
  primary: '#00A651',     // Renaissance Green
  primaryLight: '#4CAF50', // Lighter green
  primaryDark: '#00762D',  // Darker green
  accent: '#FF8C00',      // Renaissance Orange
  accentLight: '#FFB74D', // Lighter orange
  accentDark: '#E65100',  // Darker orange
  gold: '#FFD700',        // Gold accent
};

// Extended color palette for fuel monitoring context
const fuelColors = {
  critical: '#F44336', // Red for critical alerts
  warning: '#FF8C00', // Renaissance Orange for warnings
  info: '#2196F3',    // Blue for information
  success: '#00A651', // Renaissance Green for success
  fuel: {
    diesel: '#FFB74D',     // Light orange for diesel
    petrol: '#42A5F5',     // Light blue for petrol
    empty: '#E0E0E0',      // Grey for empty tanks
    contaminated: '#E57373', // Light red for contaminated fuel
  },
  generator: {
    running: '#00A651',    // Renaissance Green for running
    stopped: '#BDBDBD',    // Grey for stopped
    maintenance: '#FF8C00', // Renaissance Orange for maintenance
  }
};

const baseThemeOptions: ThemeOptions = {
  palette: {
    primary: {
      main: renaissanceColors.primary, // Renaissance Green
      light: renaissanceColors.primaryLight,
      dark: renaissanceColors.primaryDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: renaissanceColors.accent, // Renaissance Orange
      light: renaissanceColors.accentLight,
      dark: renaissanceColors.accentDark,
      contrastText: '#ffffff',
    },
    error: {
      main: fuelColors.critical,
      light: '#ffebee',
      dark: '#c62828',
    },
    warning: {
      main: fuelColors.warning,
      light: '#fff3e0',
      dark: '#ef6c00',
    },
    info: {
      main: fuelColors.info,
      light: '#e3f2fd',
      dark: '#1565c0',
    },
    success: {
      main: fuelColors.success,
      light: '#e8f5e8',
      dark: '#2e7d32',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    divider: 'rgba(0, 0, 0, 0.06)',
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    grey: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
  },
  typography: {
    fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '2.75rem',
      fontWeight: 700,
      color: '#1e293b',
      letterSpacing: '-0.025em',
    },
    h2: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '2.25rem',
      fontWeight: 700,
      color: '#1e293b',
      letterSpacing: '-0.025em',
    },
    h3: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '1.875rem',
      fontWeight: 600,
      color: '#1e293b',
      letterSpacing: '-0.025em',
    },
    h4: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#1e293b',
      letterSpacing: '-0.025em',
    },
    h5: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#334155',
      letterSpacing: '-0.025em',
    },
    h6: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '1rem',
      fontWeight: 600,
      color: '#475569',
      letterSpacing: '0.025em',
    },
    body1: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '1rem',
      fontWeight: 400,
      color: '#334155',
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '0.875rem',
      fontWeight: 400,
      color: '#64748b',
      lineHeight: 1.6,
    },
    caption: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '0.75rem',
      fontWeight: 500,
      color: '#94a3b8',
      letterSpacing: '0.05em',
    },
    subtitle1: {
      fontFamily: '"Montserrat", sans-serif',
    },
    subtitle2: {
      fontFamily: '"Montserrat", sans-serif',
    },
    overline: {
      fontFamily: '"Montserrat", sans-serif',
    },
    button: {
      fontFamily: '"Montserrat", sans-serif',
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  spacing: 8,
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          borderRadius: '16px',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 20px',
          fontSize: '0.875rem',
          lineHeight: 1.5,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          '&:hover': {
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: 'rgba(102, 126, 234, 0.04)',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(102, 126, 234, 0.04)',
          },
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.8125rem',
        },
        sizeLarge: {
          padding: '14px 28px',
          fontSize: '1rem',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontWeight: 600,
          fontSize: '0.75rem',
          height: '28px',
        },
        filled: {
          '&.MuiChip-colorPrimary': {
            background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: '16px',
        },
        elevation1: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
        elevation2: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
        elevation3: {
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#667eea',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#667eea',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
        },
      },
    },
    // Dropdown options default to a single line and are clipped by the menu edge,
    // so a long label such as "Suleja Bulk Plant — Tank C - Generator Day Tank"
    // was cut off on phones. Let them wrap instead.
    MuiMenuItem: {
      styleOverrides: {
        root: {
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          lineHeight: 1.4,
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          border: 'none',
        },
        standard: {
          '&.MuiAlert-standardSuccess': {
            backgroundColor: '#e8f5e8',
            color: '#2e7d32',
          },
          '&.MuiAlert-standardError': {
            backgroundColor: '#ffebee',
            color: '#c62828',
          },
          '&.MuiAlert-standardWarning': {
            backgroundColor: '#fff3e0',
            color: '#ef6c00',
          },
          '&.MuiAlert-standardInfo': {
            backgroundColor: '#e3f2fd',
            color: '#1565c0',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTab-root': {
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'none',
            minHeight: '48px',
            borderRadius: '12px',
            margin: '0 4px',
            transition: 'all 0.2s ease-in-out',
            '&.Mui-selected': {
              background: 'linear-gradient(45deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              color: '#667eea',
            },
            '&:hover': {
              backgroundColor: 'rgba(102, 126, 234, 0.04)',
            },
          },
        },
        indicator: {
          background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
          height: '3px',
          borderRadius: '2px',
        },
      },
    },
  },
};

// Main theme with yellow and green
export const defaultTheme = createTheme(baseThemeOptions);

// Alternative theme options that can be selected in settings
export const alternativeThemes = {
  blueGrey: createTheme({
    ...baseThemeOptions,
    palette: {
      ...baseThemeOptions.palette,
      primary: {
        main: '#2196F3',
        light: '#64B5F6',
        dark: '#1976D2',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#607D8B',
        light: '#90A4AE',
        dark: '#455A64',
        contrastText: '#ffffff',
      },
    },
  }),

  purpleDeep: createTheme({
    ...baseThemeOptions,
    palette: {
      ...baseThemeOptions.palette,
      primary: {
        main: '#673AB7',
        light: '#9575CD',
        dark: '#512DA8',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#009688',
        light: '#4DB6AC',
        dark: '#00695C',
        contrastText: '#ffffff',
      },
    },
  }),

  darkMode: createTheme({
    ...baseThemeOptions,
    palette: {
      ...baseThemeOptions.palette,
      mode: 'dark',
      primary: {
        main: bluePalette[400],
        light: bluePalette[200],
        dark: bluePalette[600],
        contrastText: '#ffffff',
      },
      secondary: {
        main: greenPalette[400],
        light: greenPalette[200],
        dark: greenPalette[600],
        contrastText: '#000000',
      },
      background: {
        default: '#121212',
        paper: '#1E1E1E',
      },
    },
  }),
};

// Extended theme with custom fuel monitoring properties
declare module '@mui/material/styles' {
  interface Theme {
    fuelColors: typeof fuelColors;
  }
  interface ThemeOptions {
    fuelColors?: typeof fuelColors;
  }
}

// Add custom fuel colors to all themes
export const fuelMonitoringTheme = createTheme({
  ...defaultTheme,
  fuelColors,
});

export { fuelColors, renaissanceColors };
export default fuelMonitoringTheme;